import { useState, useEffect, useCallback, lazy, Suspense } from 'react'
import NarrativeModal from '../components/NarrativeModal'
import { useListKeyboardNav } from '../lib/useListKeyboardNav'
import {
  Shield, Bug, AlertTriangle, RefreshCw, Search, Skull, Zap, ExternalLink,
  Flame, TrendingUp, Target, Clock, Globe as GlobeIcon, Terminal, Sparkles,
} from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { Link, useSearchParams } from 'react-router-dom'
import ShareViewButton from '../components/ShareViewButton'
import PoCFeed from '../components/PoCFeed'
import EpssMovers from '../components/EpssMovers'
import TTEHeadline from '../components/TTEHeadline'
import { api } from '../lib/api'
// Lazy-load heavy components so they don't block first paint.
// CyberGlobe pulls in globe.gl + three.js (~1.7 MB) — its chunk shouldn't
// gate the cyber-landing LCP.
const CyberGlobe     = lazy(() => import('../components/CyberGlobe'))
const CyberTerminal  = lazy(() => import('../components/CyberTerminal'))
const AttackTimeline = lazy(() => import('../components/AttackTimeline'))

const SEVERITY_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  critical: { bg: 'bg-rose/10', text: 'text-rose-light', border: 'border-rose/30' },
  high:     { bg: 'bg-amber/10', text: 'text-amber-light', border: 'border-amber/30' },
  medium:   { bg: 'bg-blue/10', text: 'text-blue-light', border: 'border-blue/30' },
  low:      { bg: 'bg-emerald/10', text: 'text-emerald-light', border: 'border-emerald/30' },
}

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

function GeopoliticalHeat({ data }: { data: any }) {
  const [expanded, setExpanded] = useState<string | null>(null)
  const maxHeat = Math.max(1, ...data.top_hot.map((x: any) => x.heat))

  const tone = (s: string) =>
    s === 'Russia'      ? { bg: 'bg-rose/20',    text: 'text-rose-light',    border: 'border-rose/30',    bar: 'bg-rose' } :
    s === 'China'       ? { bg: 'bg-amber/20',   text: 'text-amber-light',   border: 'border-amber/30',   bar: 'bg-amber' } :
    s === 'North Korea' ? { bg: 'bg-purple/20',  text: 'text-purple-light',  border: 'border-purple/30',  bar: 'bg-purple-400' } :
    s === 'Iran'        ? { bg: 'bg-cyan/20',    text: 'text-cyan-light',    border: 'border-cyan/30',    bar: 'bg-cyan' } :
                          { bg: 'bg-blue/20',    text: 'text-blue-light',    border: 'border-blue/30',    bar: 'bg-blue' }

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <GlobeIcon size={14} className="text-amber" /> Geopolitical Heat
          <span className="text-[10px] text-text-muted font-normal">
            {data.top_hot.length} actors · click for detail
          </span>
        </h2>
      </div>

      {/* Country news volume bar */}
      <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b border-border/60">
        {(data.countries || []).slice(0, 6).map((c: any) => (
          <div key={c.country} className="flex-1 min-w-[120px] bg-surface-hover/50 rounded p-2">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[11px] font-medium text-text-primary">{c.country}</span>
              <span className={clsx('text-[10px] font-semibold',
                c.delta_pct > 50  ? 'text-rose-light' :
                c.delta_pct > 0   ? 'text-amber-light' :
                c.delta_pct < 0   ? 'text-emerald' :
                                    'text-text-muted',
              )}>
                {c.recent} · {c.delta_pct > 0 ? '+' : ''}{c.delta_pct}%
              </span>
            </div>
            {c.samples && c.samples.length > 0 && (
              <div className="text-[9px] text-text-muted leading-tight mt-1 line-clamp-2" title={c.samples.slice(0, 3).map((s: any) => s.title).join(' · ')}>
                {c.samples[0].title}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Actor grid — click to expand */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {data.top_hot.map((a: any) => {
          const t = tone(a.sponsor)
          const pct = (a.heat / maxHeat) * 100
          const isOpen = expanded === a.name
          return (
            <div key={a.name} className={clsx('rounded border p-2.5 transition-all cursor-pointer', t.bg, t.border, isOpen && 'ring-1 ring-blue-light')}
                 onClick={() => setExpanded(isOpen ? null : a.name)}>
              <div className="flex items-baseline justify-between gap-2">
                <span className={clsx('font-mono text-xs font-bold', t.text)}>{a.name}</span>
                <span className="text-[10px] text-text-muted">{a.sponsor}</span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <div className="flex-1 h-1 bg-surface-hover rounded-full overflow-hidden">
                  <div className={clsx('h-full rounded-full', t.bar)} style={{ width: `${pct}%` }} />
                </div>
                <span className="text-[10px] font-mono text-text-muted">{a.heat}</span>
              </div>
              {a.reasons && a.reasons.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-[10px] text-text-secondary leading-tight">
                  {a.reasons.map((r: string, i: number) => (
                    <li key={i} className="before:content-['\\00B7'] before:mr-1 before:text-text-muted">{r}</li>
                  ))}
                </ul>
              )}

              {a.malware && a.malware.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {a.malware.slice(0, 4).map((m: string) => (
                    <span key={m} className="text-[9px] px-1.5 py-0.5 rounded bg-surface-hover/60 text-text-secondary font-mono whitespace-nowrap">
                      {m}
                    </span>
                  ))}
                  {a.malware.length > 4 && (
                    <span className="text-[9px] px-1 py-0.5 text-text-muted">+{a.malware.length - 4}</span>
                  )}
                </div>
              )}

              {(a.cve_samples && a.cve_samples.length > 0) && (
                <div className="mt-1.5 text-[10px] text-text-muted">
                  Recent CVEs:
                  {a.cve_samples.slice(0, 2).map((c: any) => (
                    <Link key={c.cve_id} to={`/app/cve/${encodeURIComponent(c.cve_id)}`}
                          onClick={e => e.stopPropagation()}
                          className="ml-1 font-mono text-blue-light hover:underline">
                      {c.cve_id}
                      {c.kev && <span className="text-rose-light ml-0.5">·KEV</span>}
                    </Link>
                  ))}
                </div>
              )}

              {(a.ransom_samples && a.ransom_samples.length > 0) && (
                <div className="mt-1 text-[10px] text-text-muted">
                  Recent victims:&nbsp;
                  <span className="text-text-secondary">
                    {a.ransom_samples.slice(0, 2).map((v: any) => v.victim).join(' · ')}
                  </span>
                </div>
              )}

              {isOpen && (
                <div className="mt-2 pt-2 border-t border-border/60 text-[10px] space-y-2" onClick={e => e.stopPropagation()}>
                  {a.cve_samples && a.cve_samples.length > 0 && (
                    <div>
                      <div className="text-text-muted uppercase tracking-wider text-[9px] mb-1">All recent CVE hits</div>
                      {a.cve_samples.map((c: any) => (
                        <Link key={c.cve_id} to={`/app/cve/${encodeURIComponent(c.cve_id)}`}
                              className="block text-text-secondary hover:text-blue-light mt-0.5">
                          <span className="font-mono text-blue-light">{c.cve_id}</span>
                          {c.cvss ? ` · CVSS ${c.cvss}` : ''}
                          {c.kev && <span className="text-rose-light"> · KEV</span>}
                          {c.summary && <div className="text-text-muted text-[9px] ml-3 mt-0.5">{c.summary}</div>}
                        </Link>
                      ))}
                    </div>
                  )}
                  {a.ransom_samples && a.ransom_samples.length > 0 && (
                    <div>
                      <div className="text-text-muted uppercase tracking-wider text-[9px] mb-1">All recent victims</div>
                      {a.ransom_samples.map((v: any, i: number) => (
                        <div key={i} className="text-text-secondary mt-0.5 truncate" title={v.victim}>
                          · {v.victim} <span className="text-text-muted">{v.country}{v.sector ? ` · ${v.sector}` : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {a.malware && a.malware.length > 4 && (
                    <div>
                      <div className="text-text-muted uppercase tracking-wider text-[9px] mb-1">All known malware ({a.malware.length})</div>
                      <div className="flex flex-wrap gap-1">
                        {a.malware.map((m: string) => (
                          <span key={m} className="px-1.5 py-0.5 rounded bg-surface-hover text-text-secondary text-[9px] font-mono">{m}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <p className="text-[9px] text-text-muted mt-3 italic">
        Heat = sponsor-country news volume × direction of change × recent CVE / leak-site activity.
        Click any card to see the evidence: recent CVE hits mentioning the actor's malware, fresh
        leak-site victims, and known tooling.
      </p>
    </div>
  )
}

function RiskBar({ score }: { score: number }) {
  const pct = Math.max(0, Math.min(100, score))
  const color = pct >= 70 ? 'bg-rose' : pct >= 50 ? 'bg-amber' : pct >= 30 ? 'bg-blue' : 'bg-emerald'
  return (
    <div className="w-full h-1.5 bg-surface-hover rounded-full overflow-hidden">
      <div className={clsx('h-full rounded-full transition-all', color)} style={{ width: `${pct}%` }} />
    </div>
  )
}

export default function CyberPage() {
  // URL state — every filter is shareable. Bookmarking the URL restores the view.
  const [searchParams, setSearchParams] = useSearchParams()
  const urlDays = parseInt(searchParams.get('days') || '30', 10)
  const urlSort = (searchParams.get('sort') || 'date') as 'date' | 'severity' | 'risk' | 'kev'
  const urlSev  = searchParams.get('severity') || 'all'
  const urlQ    = searchParams.get('q') || ''

  const [cves, setCves] = useState<any[]>([])
  const [alerts, setAlerts] = useState<any[]>([])
  const [threatLevel, setThreatLevel] = useState<any>(null)
  const [topRisk, setTopRisk] = useState<any[]>([])
  const [actorMap, setActorMap] = useState<Record<string, { name: string; sponsor: string; category: string }[]>>({})
  const [topRiskDays, setTopRiskDays] = useState<number>(Number.isFinite(urlDays) ? urlDays : 30)
  const [topRiskSort, setTopRiskSort] = useState<'date' | 'severity' | 'risk' | 'kev'>(urlSort)
  const [topRiskShowAll, setTopRiskShowAll] = useState(false)
  const [severityFilter, setSeverityFilter] = useState(urlSev)
  const [searchTerm, setSearchTerm] = useState(urlQ)

  // Watched vendors — pinned to top, persisted to user preferences via /api/me.
  // Stored as comma-separated lower-case vendor names (e.g. "microsoft,cisco,fortinet").
  const [watchedVendors, setWatchedVendors] = useState<string[]>([])
  const [watchInput, setWatchInput] = useState('')

  // Load preferences on mount; ignore 401 (anon user)
  useEffect(() => {
    api.get('/api/me')
      .then(r => setWatchedVendors((r.data?.preferences?.watched_vendors || []).map((v: string) => v.toLowerCase())))
      .catch(() => { /* anon — keep empty */ })
  }, [])

  const saveWatched = (next: string[]) => {
    setWatchedVendors(next)
    api.patch('/api/me/preferences', { watched_vendors: next })
      .catch(() => { /* offline / anon — local only */ })
  }
  const addWatched = () => {
    const v = watchInput.trim().toLowerCase()
    if (!v || watchedVendors.includes(v)) return
    saveWatched([...watchedVendors, v])
    setWatchInput('')
  }
  const removeWatched = (v: string) => saveWatched(watchedVendors.filter(x => x !== v))

  // Push state changes to URL (debounced for the search box). Replace, don't push,
  // so back-button doesn't slog through every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams()
      if (topRiskDays !== 30)              next.set('days', String(topRiskDays))
      if (topRiskSort !== 'date')          next.set('sort', topRiskSort)
      if (severityFilter !== 'all')        next.set('severity', severityFilter)
      if (searchTerm)                      next.set('q', searchTerm)
      setSearchParams(next, { replace: true })
    }, 150)
    return () => clearTimeout(t)
  }, [topRiskDays, topRiskSort, severityFilter, searchTerm, setSearchParams])
  const [loading, setLoading] = useState(true)
  const [advisories, setAdvisories] = useState<any[]>([])
  const [advisoryCounts, setAdvisoryCounts] = useState<Record<string, any>>({})
  const [patchTue, setPatchTue] = useState<any>(null)
  const [geoHeat, setGeoHeat] = useState<any>(null)
  const [narrativeOpen, setNarrativeOpen] = useState(false)

  // Keyboard navigation for the top-risk grid (j/k/o/↵/gg/⇧G)
  const visibleTopRisk = topRisk  // hook reads count, not the slice; simpler
  const topRiskKbd = useListKeyboardNav({
    count:  visibleTopRisk.length,
    getUrl: i => visibleTopRisk[i]?.cve_id ? `/app/cve/${encodeURIComponent(visibleTopRisk[i].cve_id)}` : undefined,
  })

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [feedRes, threatRes, topRes, advRes, ptRes, geoRes] = await Promise.allSettled([
        api.get('/api/cyber/feed', { params: { limit: 50 } }),
        api.get('/api/cyber/threat-level'),
        // Pull more rows when the window widens — lets the scroll actually reveal more
        api.get('/api/cyber/top-risk', {
          params: {
            limit: topRiskDays <= 30 ? 40 : topRiskDays <= 90 ? 100 : topRiskDays <= 365 ? 200 : 400,
            days: topRiskDays,
            sort: topRiskSort,
          },
        }),
        api.get('/api/cyber/advisories', { params: { limit: 30 } }),
        api.get('/api/cyber/patch-tuesday'),
        api.get('/api/cyber/geo-correlation', { params: { days: 14 } }),
      ])
      if (feedRes.status === 'fulfilled') {
        setCves(feedRes.value.data.cves || [])
        setAlerts(feedRes.value.data.alerts || [])
      }
      if (threatRes.status === 'fulfilled') setThreatLevel(threatRes.value.data)
      if (topRes.status === 'fulfilled') setTopRisk(topRes.value.data.top || [])
      if (advRes.status === 'fulfilled') {
        setAdvisories(advRes.value.data.advisories || [])
        setAdvisoryCounts(advRes.value.data.counts || {})
      }
      if (ptRes.status === 'fulfilled') setPatchTue(ptRes.value.data)
      if (geoRes.status === 'fulfilled') setGeoHeat(geoRes.value.data)
    } finally {
      setLoading(false)
    }
  }, [topRiskDays, topRiskSort])

  // Collapse the "show all" toggle when the filter changes so the page stays snappy
  useEffect(() => { setTopRiskShowAll(false) }, [topRiskDays, topRiskSort])

  // Once topRisk loads, batch-fetch actor attribution for the visible CVEs
  useEffect(() => {
    if (!topRisk.length) { setActorMap({}); return }
    const ids = topRisk.slice(0, 60).map(r => r.cve_id).filter(Boolean).join(',')
    if (!ids) return
    api.get('/api/cyber/cve-actor-batch', { params: { ids } })
      .then(r => setActorMap(r.data || {}))
      .catch(() => setActorMap({}))
  }, [topRisk])

  const refreshAdvisories = async () => {
    try {
      const { data } = await api.get('/api/cyber/advisories', { params: { limit: 30, refresh: true } })
      setAdvisories(data.advisories || [])
      setAdvisoryCounts(data.counts || {})
    } catch {}
  }

  // Pause polling when the tab isn't visible — avoids pointless network + render churn
  useEffect(() => {
    fetchAll()
    let timer: ReturnType<typeof setInterval> | null = null
    const start = () => { if (!timer) timer = setInterval(fetchAll, 300_000) }  // 5 min is plenty
    const stop  = () => { if (timer) { clearInterval(timer); timer = null } }
    start()
    const onVis = () => { document.hidden ? stop() : (fetchAll(), start()) }
    document.addEventListener('visibilitychange', onVis)
    return () => { stop(); document.removeEventListener('visibilitychange', onVis) }
  }, [fetchAll])

  const filteredCVEs = cves.filter(cve => {
    if (severityFilter !== 'all' && cve.severity !== severityFilter) return false
    if (searchTerm && !(cve.summary || '').toLowerCase().includes(searchTerm.toLowerCase()) && !(cve.id || '').toLowerCase().includes(searchTerm.toLowerCase())) return false
    return true
  })

  const filteredAlerts = alerts.filter(a => {
    if (severityFilter !== 'all' && a.severity !== severityFilter) return false
    if (searchTerm && !(a.title || '').toLowerCase().includes(searchTerm.toLowerCase())) return false
    return true
  })

  const threatStyle = SEVERITY_STYLES[threatLevel?.level === 'elevated' ? 'high' : (threatLevel?.level || 'medium')]

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Shield size={24} className="text-rose" /> Cyber Security
          </h1>
          <p className="text-sm text-text-secondary mt-1">Live CVE feed from NVD + CISA Known Exploited Vulnerabilities</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setNarrativeOpen(true)}
            className="btn-primary text-xs inline-flex items-center gap-1.5">
            <Sparkles size={14} /> Brief me
          </button>
          <button type="button" onClick={fetchAll} className="btn-secondary text-xs">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      <NarrativeModal open={narrativeOpen} onClose={() => setNarrativeOpen(false)} />

      <TTEHeadline />

      <Suspense fallback={<div className="card p-4 text-xs text-text-muted">Loading attack timeline…</div>}>
        <AttackTimeline />
      </Suspense>

      {threatLevel && (
        <div className={clsx('card p-5 border', threatStyle.border, threatStyle.bg)}>
          <div className="flex items-center gap-3 mb-2">
            <Shield size={20} className={threatStyle.text} />
            <h2 className={clsx('font-semibold', threatStyle.text)}>
              Threat Level: {threatLevel.level.toUpperCase()}
            </h2>
          </div>
          <p className="text-sm text-text-secondary">{threatLevel.summary}</p>
          <div className="flex gap-6 mt-3 text-sm">
            <div><span className="text-text-muted">Critical CVEs (7d)</span><p className="font-bold">{threatLevel.critical_cves_7d}</p></div>
            <div><span className="text-text-muted">KEV Additions (7d)</span><p className="font-bold">{threatLevel.kev_additions_7d}</p></div>
            <div><span className="text-text-muted">Ransomware-Linked</span><p className="font-bold">{threatLevel.ransomware_linked}</p></div>
          </div>
        </div>
      )}

      {patchTue && (
        <div className="card p-4 flex flex-wrap items-center gap-5">
          <div className="flex items-center gap-3">
            <div className={clsx(
              'flex flex-col items-center justify-center rounded-lg border w-20 h-20 font-mono',
              patchTue.days_until <= 2 ? 'border-rose/40 bg-rose/10' :
              patchTue.days_until <= 7 ? 'border-amber/40 bg-amber/10' :
                                         'border-border bg-surface-hover',
            )}>
              <div className={clsx('text-3xl font-bold',
                patchTue.days_until <= 2 ? 'text-rose-light' :
                patchTue.days_until <= 7 ? 'text-amber-light' :
                                           'text-text-primary')}>{patchTue.days_until}</div>
              <div className="text-[9px] uppercase tracking-wider text-text-muted">days</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-text-muted">Next Patch Tuesday</div>
              <div className="text-base font-bold text-text-primary">{patchTue.next}</div>
              <div className="text-[10px] text-text-muted">Microsoft releases fixes 2nd Tue of each month</div>
            </div>
          </div>

          <div className="border-l border-border pl-5">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Last drop · {patchTue.last}</div>
            <div className="flex items-baseline gap-3 mt-1">
              <span className="text-xl font-bold text-text-primary">{patchTue.last_summary?.count ?? 0}</span>
              <span className="text-[11px] text-text-secondary">fixes</span>
              {(patchTue.last_summary?.critical ?? 0) > 0 && (
                <span className="text-[11px] text-rose-light font-semibold">{patchTue.last_summary.critical} critical</span>
              )}
              {(patchTue.last_summary?.high ?? 0) > 0 && (
                <span className="text-[11px] text-amber-light">{patchTue.last_summary.high} high</span>
              )}
            </div>
            {patchTue.last_summary?.top_products?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {patchTue.last_summary.top_products.map((p: any) => (
                  <span key={p.name} className="text-[10px] px-1.5 py-0.5 rounded bg-blue/10 text-blue-light border border-blue/20">
                    {p.name} · {p.n}
                  </span>
                ))}
              </div>
            )}
          </div>

          <a href={patchTue.msrc_search_url} target="_blank" rel="noopener"
             className="ml-auto text-[11px] text-blue-light hover:underline flex items-center gap-1">
            <ExternalLink size={11} /> MSRC update guide
          </a>
        </div>
      )}

      {geoHeat && geoHeat.top_hot && geoHeat.top_hot.length > 0 && (
        <GeopoliticalHeat data={geoHeat} />
      )}

      {topRisk.length > 0 && (
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
              <Flame size={16} className="text-rose" /> Top Risk-Scored CVEs
              <span className="text-[10px] text-text-muted font-normal">CVSS × EPSS × KEV × breadth</span>
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-0 rounded-md border border-border overflow-hidden text-[10px]">
                {([
                  { label: '30d', v: 30 },
                  { label: '90d', v: 90 },
                  { label: '1yr', v: 365 },
                  { label: '5yr', v: 1825 },
                ] as const).map(opt => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setTopRiskDays(opt.v)}
                    className={clsx(
                      'px-2 py-1 transition-colors',
                      topRiskDays === opt.v
                        ? 'bg-rose/20 text-rose-light font-semibold'
                        : 'text-text-muted hover:bg-surface-hover',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-0 rounded-md border border-border overflow-hidden text-[10px]">
                {([
                  { label: 'Date',     v: 'date' as const,     hint: 'Newest first' },
                  { label: 'Severity', v: 'severity' as const, hint: 'Critical → Low' },
                  { label: 'Risk',     v: 'risk' as const,     hint: 'Risk score DESC' },
                  { label: 'KEV',      v: 'kev' as const,      hint: 'Exploited in wild first' },
                ]).map(opt => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setTopRiskSort(opt.v)}
                    title={opt.hint}
                    className={clsx(
                      'px-2 py-1 transition-colors',
                      topRiskSort === opt.v
                        ? 'bg-blue/20 text-blue-light font-semibold'
                        : 'text-text-muted hover:bg-surface-hover',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <span className="text-[10px] text-text-muted">{topRisk.length} shown</span>
            </div>
          </div>
          {/* Watched-vendor manager — pinned vendors hoist matching CVEs to the top */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-text-muted">Watched vendors</span>
            {watchedVendors.map(v => (
              <span key={v} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue/15 text-blue-light border border-blue/40 text-[10px] font-mono">
                {v}
                <button type="button" onClick={() => removeWatched(v)}
                  className="text-blue-light/60 hover:text-blue-light" title="Remove">×</button>
              </span>
            ))}
            <input value={watchInput}
              onChange={e => setWatchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addWatched()}
              placeholder="add vendor (e.g. microsoft)"
              className="input py-1 text-[10px] w-44" />
            <button type="button" onClick={addWatched} className="btn-ghost text-[10px]">+ add</button>
            {watchedVendors.length > 0 && (
              <span className="text-[9px] text-text-muted ml-auto">
                {topRisk.filter(r => watchedVendors.some(w =>
                  (r.affected_products || []).some((p: string) => p.toLowerCase().includes(w))
                )).length} CVE{topRisk.filter(r => watchedVendors.some(w =>
                  (r.affected_products || []).some((p: string) => p.toLowerCase().includes(w))
                )).length === 1 ? '' : 's'} match watched
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 max-h-[520px] overflow-y-auto pr-1">
            {(topRiskShowAll ? topRisk : topRisk.slice(0, 40))
              .slice() // copy before sort
              .sort((a, b) => {
                const aw = watchedVendors.length > 0 && watchedVendors.some(w =>
                  (a.affected_products || []).some((p: string) => p.toLowerCase().includes(w))) ? 0 : 1
                const bw = watchedVendors.length > 0 && watchedVendors.some(w =>
                  (b.affected_products || []).some((p: string) => p.toLowerCase().includes(w))) ? 0 : 1
                return aw - bw
              })
              .map((r, idx) => {
              const s = SEVERITY_STYLES[r.severity] || SEVERITY_STYLES.medium
              const epssPct = r.epss_percentile != null ? Math.round(r.epss_percentile * 100) : null
              const kbd = topRiskKbd.getRowProps(idx)
              return (
                <Link
                  key={r.cve_id}
                  ref={kbd.ref as any}
                  aria-selected={kbd['aria-selected']}
                  onMouseEnter={kbd.onMouseEnter}
                  to={`/app/cve/${encodeURIComponent(r.cve_id)}`}
                  className={clsx('block p-3 rounded-lg border transition-all hover:scale-[1.02]', s.border, s.bg, kbd.className)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={clsx('font-mono text-xs font-bold', s.text)}>{r.cve_id}</span>
                    <div className="flex items-center gap-1">
                      {r.kev && <span className="badge-red text-[9px]"><Zap size={9} /> KEV</span>}
                      {typeof r.weaponization_days === 'number' && r.weaponization_days <= 7 && (
                        <span className="badge text-[9px] bg-rose/20 text-rose-light border border-rose/40"
                          title={`Disclosed → KEV in ${r.weaponization_days} days`}>
                          ⚠ {r.weaponization_days}d
                        </span>
                      )}
                      {(actorMap[r.cve_id]?.length ?? 0) > 0 && (
                        <span
                          className="badge text-[9px] bg-purple-500/15 text-purple-300 border border-purple-500/30"
                          title={(actorMap[r.cve_id] || []).map(a => `${a.name} (${a.sponsor})`).join('\n')}
                        >
                          ✓ {actorMap[r.cve_id].length} actor{actorMap[r.cve_id].length === 1 ? '' : 's'}
                        </span>
                      )}
                      <span className={clsx('badge text-[9px]', s.bg, s.text)}>{r.cvss_score ?? '?'}</span>
                    </div>
                  </div>
                  <p className="text-xs text-text-primary leading-snug line-clamp-2 mb-2" title={r.summary}>{r.summary}</p>
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-[10px] mb-1">
                      <span className="text-text-muted flex items-center gap-1"><Target size={10} /> Risk</span>
                      <span className={clsx('font-bold', s.text)}>{r.risk_score}/100</span>
                    </div>
                    <RiskBar score={r.risk_score} />
                    {r.effective_risk != null && r.credit_total > 0 && (
                      <div className="mt-1.5">
                        <div className="flex items-center justify-between text-[10px] mb-0.5">
                          <span
                            className="text-emerald-light flex items-center gap-1"
                            title={(r.controls || []).map((c: any) => `${c.credit > 0 ? '-' : '+'}${c.credit}  ${c.control}`).join('\n')}
                          >
                            <Shield size={9} /> After controls
                          </span>
                          <span className="font-bold text-emerald-light">
                            {r.effective_risk}/100 <span className="text-text-muted font-normal">(-{r.credit_total})</span>
                          </span>
                        </div>
                        <RiskBar score={r.effective_risk} />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2 text-[10px] text-text-muted">
                    <span className="flex items-center gap-1">
                      <TrendingUp size={10} /> EPSS {epssPct != null ? `${epssPct}%` : '—'}
                    </span>
                    <span>{r.affected_count} product{r.affected_count === 1 ? '' : 's'}</span>
                  </div>
                  {r.published_at && (
                    <div
                      className="mt-2 pt-2 border-t border-border/50 text-[11px] text-text-secondary flex items-center gap-1.5"
                      title={new Date(r.published_at).toLocaleString()}
                    >
                      <Clock size={11} className="text-text-muted" />
                      <span className="font-medium">{relativeTime(r.published_at)}</span>
                      <span className="text-text-muted">·</span>
                      <span className="font-mono text-text-muted">{r.published_at.slice(0, 10)}</span>
                    </div>
                  )}
                </Link>
              )
            })}
          </div>
          {topRisk.length > 40 && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={() => setTopRiskShowAll(v => !v)}
                className="btn-secondary text-xs"
              >
                {topRiskShowAll ? `Collapse to 40` : `Show all ${topRisk.length}`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Threat Map + Cyber Terminal ────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="card p-4 xl:col-span-2 min-w-0">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
            <GlobeIcon size={16} className="text-rose" /> Threat Actor Globe
            <span className="text-[10px] text-text-muted font-normal">
              attackers · subsea cables · KEV targets
            </span>
          </h2>
          <Suspense fallback={<div className="h-[460px] flex items-center justify-center text-text-muted text-xs">Loading 3D globe…</div>}>
            <CyberGlobe height={460} />
          </Suspense>
        </div>
        <div className="card p-4 min-w-0">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
            <Terminal size={16} className="text-emerald" /> Cyber Console
            <span className="text-[10px] text-text-muted font-normal">
              interactive CVE / KEV / actor lookup
            </span>
          </h2>
          <Suspense fallback={<div className="h-[460px] flex items-center justify-center text-text-muted text-xs">Loading console…</div>}>
            <CyberTerminal height={460} />
          </Suspense>
        </div>
      </div>

      {/* ── Public PoCs (DB-backed, hourly scan) ──────────────────────────── */}
      <PoCFeed days={14} limit={12} />

      {/* ── EPSS movers (predictive trajectory, daily snapshot) ───────────── */}
      <EpssMovers windowDays={7} limit={12} />

      {/* ── Vendor Advisories ─────────────────────────────────────────────── */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber" /> Vendor Advisories
            <span className="text-[10px] text-text-muted font-normal">
              Microsoft MSRC · Red Hat · Cisco · IBM
            </span>
          </h2>
          <div className="flex items-center gap-3">
            {Object.entries(advisoryCounts).filter(([k]) => k !== 'total').map(([vendor, c]: [string, any]) => (
              <span key={vendor} className="text-[10px] text-text-muted">
                {vendor}: <b className="text-text-primary">{c.total}</b>
                {c.critical_high > 0 && <span className="text-rose-light"> · {c.critical_high} hi</span>}
              </span>
            ))}
            <button type="button" onClick={refreshAdvisories} className="btn-secondary text-[10px] px-2 py-1" title="Re-fetch from upstream">
              <RefreshCw size={10} /> Refresh
            </button>
          </div>
        </div>
        <div className="space-y-2 max-h-[380px] overflow-y-auto">
          {advisories.length === 0 && (
            <div className="text-xs text-text-muted text-center py-6">
              No advisories yet — click <b>Refresh</b> to pull from MSRC / Red Hat / Cisco / IBM
            </div>
          )}
          {advisories.map((a: any, idx) => {
            const sev = (a.severity || '').toLowerCase()
            const sStyle = SEVERITY_STYLES[sev] || SEVERITY_STYLES.medium
            return (
              <a
                key={a.id || idx}
                href={a.url}
                target="_blank"
                rel="noopener"
                className="block p-3 rounded-lg border border-border bg-surface-card hover:bg-surface-hover transition-colors"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2">
                    <span className="badge text-[9px] bg-blue/10 text-blue-light border border-blue/30">{a.vendor}</span>
                    {sev && <span className={clsx('badge text-[9px]', sStyle.bg, sStyle.text)}>{sev}</span>}
                    {(a.cve_ids || []).slice(0, 3).map((cve: string) => (
                      <Link key={cve} to={`/app/cve/${encodeURIComponent(cve)}`} onClick={e => e.stopPropagation()} className="font-mono text-[10px] text-blue-light hover:underline">
                        {cve}
                      </Link>
                    ))}
                  </div>
                  <span className="text-[10px] text-text-muted">{(a.published_at || '').slice(0, 10)}</span>
                </div>
                <p className="text-xs text-text-primary leading-snug">{a.title}</p>
                {a.summary && <p className="text-[11px] text-text-secondary leading-relaxed mt-1 line-clamp-2">{a.summary}</p>}
              </a>
            )
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input data-kbd-search value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search CVEs, alerts… (press /)" className="input pl-9 py-2 text-xs" />
        </div>
        <select
          aria-label="Filter by severity"
          title="Filter by severity"
          value={severityFilter}
          onChange={e => setSeverityFilter(e.target.value)}
          className="input w-auto py-2 text-xs"
        >
          <option value="all">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <ShareViewButton />
      </div>

      {loading && !cves.length && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Fetching CVEs from NVD...</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-4">
            <Bug size={16} className="text-rose" /> Recent CVEs (NVD)
            <span className="badge-red text-[10px] ml-auto">{filteredCVEs.filter(c => c.exploited_in_wild).length} exploited</span>
          </h2>
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {filteredCVEs.map((cve, idx) => {
              const s = SEVERITY_STYLES[cve.severity] || SEVERITY_STYLES.medium
              return (
                <div key={cve.id || idx} className={clsx('p-3 rounded-lg border', s.border, s.bg)}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Link to={`/app/cve/${encodeURIComponent(cve.id)}`} className={clsx('font-mono text-xs font-bold hover:underline', s.text)}>{cve.id}</Link>
                      <a href={cve.url} target="_blank" rel="noopener" className="text-text-muted hover:text-blue-light" title="Open on NVD"><ExternalLink size={10} /></a>
                    </div>
                    <div className="flex items-center gap-2">
                      {cve.exploited_in_wild && <span className="badge-red text-[10px]"><Zap size={10} /> In the Wild</span>}
                      <span className={clsx('badge text-[10px]', s.bg, s.text)}>CVSS {cve.cvss_score}</span>
                    </div>
                  </div>
                  <p className="text-sm text-text-primary leading-snug">{cve.summary}</p>
                  <div className="flex items-center gap-2 mt-2 text-[10px] text-text-muted">
                    <span>{(cve.affected_products || []).join(', ')}</span>
                    <span>&middot;</span>
                    <span>{relativeTime(cve.published)}</span>
                  </div>
                </div>
              )
            })}
            {!loading && filteredCVEs.length === 0 && <p className="text-xs text-text-muted">No CVEs match filters</p>}
          </div>
        </div>

        <div className="card p-5">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-4">
            <AlertTriangle size={16} className="text-amber" /> CISA Exploited Vulnerabilities
          </h2>
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {filteredAlerts.map((alert, idx) => {
              const s = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.high
              return (
                <div key={alert.id || idx} className="p-3 rounded-lg border border-border bg-surface-card hover:bg-surface-hover transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <Skull size={14} className={s.text} />
                    <span className={clsx('badge text-[10px]', s.bg, s.text)}>{alert.severity}</span>
                    {alert.ransomware_use === 'Known' && <span className="badge-red text-[10px]">Ransomware</span>}
                  </div>
                  <p className="text-sm font-medium text-text-primary leading-snug">{alert.title}</p>
                  <p className="text-xs text-text-secondary mt-1 leading-relaxed">{alert.summary}</p>
                  <div className="flex items-center gap-2 mt-2 text-[10px] text-text-muted">
                    <span>{alert.source}</span>
                    <span>&middot;</span>
                    <span>{relativeTime(alert.timestamp)}</span>
                  </div>
                </div>
              )
            })}
            {!loading && filteredAlerts.length === 0 && <p className="text-xs text-text-muted">No alerts match filters</p>}
          </div>
        </div>
      </div>
    </div>
  )
}
